import { BulkLoad, SingleLoad } from '@react-pdf/types/font';
import styled, {
  FlattenInterpolation,
  SimpleInterpolation,
  StyledComponent,
  ThemedStyledProps,
  ThemeProps,
} from 'styled-components';

// Augment the styled-components default theme to enable strict type
// checking when the theme is used within a styled component.
declare module 'styled-components' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  export interface DefaultTheme extends Theme {}
}

export type Font =
  | 'headingXLarge'
  | 'headingLarge'
  | 'headingMedium'
  | 'headingSmall'
  | 'headingXSmall'
  | 'paragraphLarge'
  | 'paragraph'
  | 'paragraphSmall'
  | 'paragraphStyled';

export type Fonts = {
  [T in Font]: SimpleInterpolation;
} & { pdfFontFaces?: BulkLoad | SingleLoad };

export type Color =
  | 'core01'
  | 'core02'
  | 'core03'
  | 'neutral01'
  | 'neutral02'
  | 'neutral03'
  | 'neutral04'
  | 'neutral05'
  | 'neutral06'
  | 'neutral07'
  | 'neutral08'
  | 'cardBorder'
  | 'cardIcon'
  | 'navigationHighlight'
  | 'notificationError'
  | 'notificationErrorDark'
  | 'notificationErrorLight'
  | 'notificationWarning'
  | 'notificationWarningLight'
  | 'notificationSuccess'
  | 'notificationSuccessLight'
  | 'notificationInfo'
  | 'notificationInfoLight'
  | 'selectorHighlight'
  | 'selectorHighlightWithTransparency';

export type Colors = {
  [T in Color]: string;
};

export type Gradient = 'gradient01';

export type Gradients = {
  [T in Gradient]: {
    from: string;
    to: string;
  };
};

// List of standard icons, this can be updated with any icons
// that are required to be provided as part of the theme
export type StandardIcon =
  | 'add'
  | 'alert'
  | 'arrow-down'
  | 'arrow-up'
  | 'chevron'
  | 'close-menu'
  | 'close'
  | 'download-document'
  | 'download-generic'
  | 'edit'
  | 'error'
  | 'gb-numberplate'
  | 'included'
  | 'menu'
  | 'minus'
  | 'new-tab'
  | 'not-included'
  | 'plus'
  | 'print'
  | 'remove'
  | 'save'
  | 'speech-bubble'
  | 'swipe-left'
  | 'swipe-right'
  | 'telephone'
  | 'warning';

export type IconWithOptionalVariant = {
  default: string;
  variant?: string;
};

export type Icons = {
  [I in StandardIcon]: IconWithOptionalVariant;
} & {
  [key: string]: IconWithOptionalVariant;
};

export type Breakpoint = 'mobile' | 'tabletPortrait' | 'tabletLandscape' | 'desktop';
export type NonMobileBreakpoint = Exclude<Breakpoint, 'mobile'>;

export type Layout = {
  baseSpacingPx: number;
  mobile: {
    gutterPx: number;
    columns: number;
    containerWidthPx?: number;
  };
} & {
  [T in NonMobileBreakpoint]: {
    breakpointMinPx: number;
    gutterPx: number;
    columns: number;
    containerWidthPx?: number;
  };
};

// This covers the types of icons we use in the various buttons
type ButtonIconTypes =
  | 'cta'
  | 'ctaOpenInNewTab'
  | 'add'
  | 'back'
  | 'edit'
  | 'remove'
  | 'download'
  | 'downloadGeneric';

/**
 * ButtonIcons defines the default icons displayed on the various CL buttons. Only
 * icons belonging to the StandardIcon type are valid, as these icons are defined
 * in the CL and so are guaranteed to be present on the theme. If an icon is not
 * set, no icon will be displayed.
 */
export type ButtonIcons = { [T in ButtonIconTypes]?: StandardIcon };

export type ComponentStyle =
  // any refers to the props of any given component
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  FlattenInterpolation<ThemedStyledProps<any, Theme>> | SimpleInterpolation;

type Components = {
  [name: string]: ComponentStyle;
};

type FormLayout = {
  // Moves the additional footer buttons below next/back on mobile
  alternateMobileFooterButtons?: boolean;
};

export type Theme = {
  name: string;
  fonts: Fonts;
  fontFaces?: string;
  colors: Colors;
  gradients: Gradients;
  icons: Icons;
  layout: Layout;
  buttonIcons?: ButtonIcons;
  richTextOpenInNewTabIcon?: StandardIcon;
  externalOnlyRichTextOpenInNewTabIcon?: StandardIcon;
  components?: Components;
  global?: ComponentStyle;
  // If we get more properties that are standalone like this, we should consider whether we
  // can nicely move the into a nested type(s).
  buttonRadius: string;
  cardRadius: string;
  inputRadius: string;
  tableRadius: string;
  formLayout?: FormLayout;
};

/**
 * Applies any custom styling defined on the theme in the 'components' section.
 *
 * @param component the component to apply the theming to
 * @param name the component name used on the theme
 */
export const applyComponentTheme = <P>(
  component: React.ComponentType<P>,
  name: string
): StyledComponent<React.ComponentType<P>, Theme> => {
  return styled(component)`
    ${({ theme }: ThemeProps<Theme>): ComponentStyle | undefined => {
      return theme.components?.[name];
    }}
  `;
};
